// DashboardUsers.jsx

import { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Divider,
  Typography,
} from "@mui/material";

import { useUsers } from "../../../hooks/useUsers";
import { useResourceGroupMembership } from "../../../hooks/useResourceGroupMembership";
import MainPanelContent from "../components/MainPanelContent";
import { SideDrawer } from "../../../components/SideDrawer";
import ResourceGroupsEditor from "./components/ResourceGroupsEditor";
import UsersTableBase from "./components/UsersTableBase";

const DashboardUsers = () => {
  const {
    users,
    loading: usersLoading,
    error: usersError,
    refreshUsers,
  } = useUsers();
  const { membershipsByUserId, refreshMemberships } =
    useResourceGroupMembership();

  const [selectedUser, setSelectedUser] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleOpenDrawer = (user) => {
    setSelectedUser(user);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedUser(null);
  };

  const formatLastSignIn = (lastSignInAt) => {
    // The RPC returns auth.users.last_sign_in_at; null means no recorded sign-in.
    if (!lastSignInAt) return "-";
    const date = new Date(lastSignInAt);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const formatCreatedDate = (createdAt) => {
    if (!createdAt) return "-";
    const date = new Date(createdAt);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  // Detail tables are shown as a compact, width-constrained card so the values
  // don't stretch across the whole (very wide) drawer. Cells get comfortable
  // padding, a fixed label column, and the last row drops its bottom border.
  const tableContainerSx = {
    maxWidth: 480,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    overflow: "hidden",
  };
  const tableSx = { "& tr:last-child td": { borderBottom: 0 } };
  const labelCellSx = {
    fontWeight: 600,
    color: "text.secondary",
    borderColor: "divider",
    py: 1,
    px: 2,
    width: 140,
    whiteSpace: "nowrap",
    verticalAlign: "top",
  };
  const valueCellSx = {
    borderColor: "divider",
    py: 1,
    px: 2,
    color: "text.primary",
    wordBreak: "break-word",
  };

  return (
    <MainPanelContent>
      <Box sx={{ width: "100%" }}>
        <UsersTableBase
          users={users}
          loading={usersLoading}
          error={usersError}
          mode="view"
          membershipsByUserId={membershipsByUserId}
          onClickDetails={handleOpenDrawer}
        />

        {selectedUser && (
          <SideDrawer
            open={isDrawerOpen}
            onClose={handleCloseDrawer}
            title="User Details"
          >
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              Authentication Status
            </Typography>
            <TableContainer sx={tableContainerSx}>
              <Table size="small" sx={tableSx}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={labelCellSx}>Last Login</TableCell>
                    <TableCell sx={valueCellSx}>
                      {formatLastSignIn(selectedUser.last_sign_in_at)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={labelCellSx}>Creation Date</TableCell>
                    <TableCell sx={valueCellSx}>
                      {formatCreatedDate(selectedUser.created_at)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              Affiliation Status
            </Typography>
            <TableContainer sx={tableContainerSx}>
              <Table size="small" sx={tableSx}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={labelCellSx}>Occupation</TableCell>
                    <TableCell sx={valueCellSx}>
                      {selectedUser.affiliation?.occupation || "N/A"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={labelCellSx}>Institution</TableCell>
                    <TableCell sx={valueCellSx}>
                      {selectedUser.affiliation?.institution || "N/A"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={labelCellSx}>Department</TableCell>
                    <TableCell sx={valueCellSx}>
                      {selectedUser.affiliation?.department || "N/A"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={labelCellSx}>Advisor</TableCell>
                    <TableCell sx={valueCellSx}>
                      {selectedUser.affiliation?.advisor || "N/A"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Divider sx={{ my: 2 }} />
            <ResourceGroupsEditor
              userId={selectedUser.id}
              onMembershipChange={async () => {
                await Promise.all([refreshUsers(), refreshMemberships()]);
              }}
            />
          </SideDrawer>
        )}
      </Box>
    </MainPanelContent>
  );
};

export default DashboardUsers;
